import { useIntl } from 'react-intl';
import { Col, Form, Input, Row, Select } from 'antd';
import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import { usePartConfigurationProfile } from '@/core/domain/stock/Configuration/Configuration.hooks';
import { PartItemFieldsEnum } from '@/core/domain/stock/Configuration/Configuration.types';
import ActiveExtraFilter from '@/components/_common/ExtraFilters/ActiveExtraFilter';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import { useEffect } from 'react';
import {
  ItemLabelFilterType,
  ItemLabelRequest,
} from '@/core/domain/stock/itemsLabel/items-label.types';

function ItemsForLabelExtraFilter({
  queryParams,
  setQueryParams,
  vertical = false,
  inModal = false,
  onMounted,
}: ExtraFiltersProps<ItemLabelRequest> & {
  vertical?: boolean;
  inModal?: boolean;
}) {
  const intl = useIntl();

  const colProps = vertical
    ? { span: 24 }
    : { span: 24, md: 12, lg: 6, xxl: !inModal ? 4 : undefined };

  const { data, dataUpdatedAt } = usePartConfigurationProfile();

  const showField = (field: PartItemFieldsEnum) => {
    return data?.result.find((profile) => profile.field === field)?.active;
  };

  useEffect(() => {
    if (onMounted) {
      onMounted();
    }
  }, [dataUpdatedAt]);

  return (
    <Row gutter={[16, 16]} key={dataUpdatedAt}>
      <Col {...colProps}>
        <Form.Item
          noStyle
          name="filterType"
          initialValue={ItemLabelFilterType.Free}
        >
          <Select
            placeholder="Filtrar por"
            style={{ width: '100%' }}
            options={[
              {
                label: 'Campo - Livre',
                value: ItemLabelFilterType.Free,
              },
              {
                label: 'Campo - Especifico',
                value: ItemLabelFilterType.Specific,
              },
            ]}
            onChange={(value) =>
              setQueryParams((prev) => ({
                ...prev,
                filterType: value,
              }))
            }
          />
        </Form.Item>
      </Col>

      {showField(PartItemFieldsEnum.BarCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.barcode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, barcode: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.barCode' })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.OriginalCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.sku}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, sku: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.originalCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.ManufacturingCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.manufacturingCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, manufacturingCode: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.manufacturerCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.companyCode) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.companyCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, companyCode: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.companyCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Description) && (
        <Col {...colProps}>
          <Input
            value={queryParams?.description}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, description: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.description' })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Manufacturer) && (
        <Col {...colProps}>
          <ManufacturerInfiniteSelectField
            allowClear
            placeholder={intl.formatMessage({
              id: 'parts.entity.manufacturer',
            })}
            queryFilter={{ active: true }}
            value={queryParams?.manufacturerId}
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, manufacturerId: value }))
            }
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Group) && (
        <Col {...colProps}>
          <GroupsInfiniteSelectField
            allowClear
            value={queryParams?.subGroupId}
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, subGroupId: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.subGroup' })}
          />
        </Col>
      )}

      <Col {...colProps}>
        <Input
          value={queryParams?.location}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, location: value }))
          }
          placeholder={intl.formatMessage({ id: 'parts.entity.location' })}
        />
      </Col>

      <Col {...colProps}>
        <OrderExtraFilter
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>

      <Col {...colProps}>
        <ActiveExtraFilter
          value={queryParams?.active}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              active: value as unknown as boolean,
            }))
          }
        />
      </Col>
    </Row>
  );
}

export default ItemsForLabelExtraFilter;
